import React, { useRef, useState, useLayoutEffect, ReactNode } from 'react';

interface ScaleToFitProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Padding inside the container to account for when calculating available space
   */
  padding?: number;
  /**
   * Minimum scale factor (default: 0.1)
   */
  minScale?: number;
  /**
   * Maximum scale factor (default: 1)
   */
  maxScale?: number;
  /**
   * When true, scales based on available width and derives the container height
   * from the scaled content. Useful when the container shouldn't reserve a fixed height.
   */
  autoHeight?: boolean;
  /**
   * Maximum container height (px). Only used when autoHeight is true.
   * If the width-based scale would exceed this, scale is reduced to fit.
   */
  maxHeight?: number;
}

/**
 * A component that automatically scales its children to fit within the container
 * using CSS transforms. Measurements happen in useLayoutEffect to prevent flickering.
 */
export function ScaleToFit({
  children,
  className = '',
  style = {},
  padding = 0,
  minScale = 0.1,
  maxScale = 1,
  autoHeight = false,
  maxHeight,
}: ScaleToFitProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(1);
  const [derivedHeight, setDerivedHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current || !contentRef.current) return;

      const container = containerRef.current;
      const content = contentRef.current;

      const containerWidth = container.clientWidth - padding * 2;

      const previousTransform = content.style.transform;
      content.style.transform = 'none';

      const contentWidth = content.scrollWidth;
      const contentHeight = content.scrollHeight;

      content.style.transform = previousTransform;

      if (autoHeight) {
        let newScale = containerWidth > 0 && contentWidth > 0 ? containerWidth / contentWidth : 1;
        newScale = Math.min(Math.max(newScale, minScale), maxScale);

        let scaledHeight = contentHeight * newScale + padding * 2;

        if (maxHeight && scaledHeight > maxHeight) {
          const availableHeight = maxHeight - padding * 2;
          const heightBasedScale = availableHeight > 0 && contentHeight > 0 ? availableHeight / contentHeight : 1;
          newScale = Math.min(Math.max(heightBasedScale, minScale), maxScale);
          scaledHeight = maxHeight;
        }

        setScale(newScale);
        setDerivedHeight(scaledHeight);
      } else {
        const containerHeight = container.clientHeight - padding * 2;
        const scaleX = containerWidth > 0 && contentWidth > 0 ? containerWidth / contentWidth : 1;
        const scaleY = containerHeight > 0 && contentHeight > 0 ? containerHeight / contentHeight : 1;
        const newScale = Math.min(Math.max(Math.min(scaleX, scaleY), minScale), maxScale);
        setScale(newScale);
      }
    };

    calculateScale();

    const resizeObserver = new ResizeObserver(() => {
      calculateScale();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [padding, minScale, maxScale, children, autoHeight, maxHeight]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        ...style,
        position: 'relative',
        overflow: 'hidden',
        ...(autoHeight && derivedHeight !== undefined ? { height: derivedHeight } : {}),
      }}
    >
      <div
        ref={contentRef}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center',
          transition: 'transform 0.3s ease-out',
          display: 'inline-flex',
          position: 'absolute',
          top: '50%',
          left: '50%',
          translate: '-50% -50%',
        }}
      >
        {children}
      </div>
    </div>
  );
}
